/**
 * Delegado Regional · Dashboard de oportunidades de zona.
 *
 * Vista dedicada para usuarios con rol `delegado_regional`. Muestra
 * únicamente las oportunidades de su zona (provincias/comunidades)
 * con score Pando ≥ score_min y deadline >= hoy.
 *
 * Permite marcar cada oportunidad como revisada/descartada con comentario.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "@/context/AuthContext";
import LanguageToggle from "@/components/LanguageToggle";
import DGFilter, { useDGFilter, DG_LABELS } from "@/components/DGFilter";
import {
  MapPin, Calendar, Euro, ExternalLink, Sparkles, AlertCircle,
  CheckCircle2, XCircle, Eye, MessageSquare, Loader2, RefreshCw, LogOut, Filter, Bell, Settings, LayoutPanelLeft, Layers, FileCheck2, AlarmClock, ChevronDown, Mail,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import PremiumAlertsSettings from "@/components/tender360/PremiumAlertsSettings";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import ResumenEjecutivoDelegado from "@/pages/delegado/ResumenEjecutivoDelegado";
import MapaZonaDelegado from "@/pages/delegado/MapaZonaDelegado";
import ComparadorOfertas from "@/pages/delegado/ComparadorOfertas";
import HistorialAnalisis from "@/pages/delegado/HistorialAnalisis";
import AlarmasContrato from "@/pages/delegado/AlarmasContrato";
import PreviewMiAlerta from "@/pages/delegado/PreviewMiAlerta";

const ESTADO_BADGE_CFG = {
  nueva:       { cls: "bg-blue-100 text-blue-700",       Icon: Sparkles },
  revisada:    { cls: "bg-emerald-100 text-emerald-700", Icon: CheckCircle2 },
  descartada:  { cls: "bg-gray-200 text-gray-500",       Icon: XCircle },
};

function scoreColor(score) {
  if (score >= 90) return { bg: "bg-rose-100",    text: "text-rose-700",    border: "border-rose-300" };
  if (score >= 80) return { bg: "bg-amber-100",   text: "text-amber-700",   border: "border-amber-300" };
  return                     { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-300" };
}

const fmtEur = (n, locale = "es-ES") =>
  n == null || Number(n) === 0 ? "—" :
  new Intl.NumberFormat(locale, { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);

function diasRestantes(deadlineIso) {
  if (!deadlineIso) return null;
  try {
    const d = new Date(deadlineIso);
    const now = new Date();
    return Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

export default function Delegado() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const locale = i18n.language?.startsWith("en") ? "en-GB" : "es-ES";
  const [me, setMe]               = useState(null);
  const [items, setItems]         = useState([]);
  const [config, setConfig]       = useState(null);
  const [loading, setLoading]     = useState(true);
  const [filterEstado, setFilterEstado] = useState("nuevas");
  const [reviewing, setReviewing] = useState(null);
  const [analyzing, setAnalyzing] = useState(null);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showSelfEdit, setShowSelfEdit] = useState(false);
  const [showMapa, setShowMapa] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selectedCompare, setSelectedCompare] = useState([]);
  const [showComparador, setShowComparador] = useState(false);
  const [showHistorial, setShowHistorial] = useState(false);
  const [showAlarmas, setShowAlarmas] = useState(false);
  const [showPreviewAlerta, setShowPreviewAlerta] = useState(false);
  const [alarmasCount, setAlarmasCount] = useState(0);
  const focusId = new URLSearchParams(location.search).get("focus");
  // iter134 · Filtro DG global
  const { dg, setDg, filterByDG } = useDGFilter(user?.email);

  const load = async () => {
    setLoading(true);
    try {
      const [meR, oppR] = await Promise.all([
        axios.get(`${API}/delegado/me`),
        axios.get(`${API}/delegado/oportunidades`, { params: { estado: filterEstado === "todas" ? undefined : filterEstado } }),
      ]);
      setMe(meR.data);
      setItems(oppR.data.items || []);
      setConfig(oppR.data.config);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("delegado.toast.loadError"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterEstado]);

  // Iter279 · badge de alarmas de contrato (>1M€, <=2 años para finalizar)
  useEffect(() => {
    axios.get(`${API}/delegado/oportunidades/alarmas-contratos`)
      .then((r) => setAlarmasCount(r.data.total || 0))
      .catch(() => {});
  }, []);

  // Auto-scroll a la oportunidad focada (desde email)
  useEffect(() => {
    if (focusId && items.length) {
      const el = document.getElementById(`opp-${focusId}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-4", "ring-emerald-400", "ring-offset-2");
        setTimeout(() => el.classList.remove("ring-4", "ring-emerald-400", "ring-offset-2"), 4000);
      }
    }
  }, [focusId, items]);

  const stats = useMemo(() => {
    const list = filterByDG(items);
    const total = list.length;
    const urg = list.filter(i => (i.ai_score || 0) >= 90).length;
    const proximas7d = list.filter(i => {
      const d = diasRestantes(i.deadline);
      return d != null && d >= 0 && d <= 7;
    }).length;
    return { total, urg, proximas7d };
  }, [items, filterByDG]);
  const itemsF = useMemo(() => filterByDG(items), [items, filterByDG]);

  if (loading && !me) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-emerald-700"><Loader2 className="animate-spin"/> {t("delegado.loadingPanel")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-4 py-3 flex items-center justify-between flex-wrap gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">{t("delegado.headerLabel")}</p>
            <h1 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <MapPin className="text-emerald-600" size={18}/>
              {me?.nombre || user?.name}
              <span className="text-xs text-slate-500 font-normal">· {(me?.provincias || []).join(", ")}</span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <DGFilter value={dg} onChange={setDg} size="sm" testidPrefix="del-dg-filter"/>
            <LanguageToggle />
            <button
              onClick={() => setShowMapa(true)}
              data-testid="delegado-mapa-zona-btn"
              title="Ver todas las oportunidades de mi zona en un mapa"
              className="text-xs bg-sky-50 hover:bg-sky-100 text-sky-800 border border-sky-200 px-3 py-1.5 rounded flex items-center gap-1 font-semibold">
              <MapPin size={12}/> Mapa de mi zona
            </button>
            <button
              onClick={() => { setCompareMode(!compareMode); setSelectedCompare([]); }}
              data-testid="delegado-compare-mode-btn"
              title="Comparar 2-3 oportunidades lado a lado"
              className={`text-xs px-3 py-1.5 rounded flex items-center gap-1 font-semibold border ${compareMode ? "bg-violet-600 text-white border-violet-600" : "bg-violet-50 hover:bg-violet-100 text-violet-800 border-violet-200"}`}>
              <Layers size={12}/> {compareMode ? "Cancelar comparación" : "Comparar ofertas"}
            </button>

            {/* Iter281 · agrupado: Historial, Alarmas de contrato y Alertas Premium
                bajo un único menú "Alertas" para no amontonar botones a 1440px */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="delegado-alertas-menu-btn"
                  title="Historial, alarmas de contrato y alertas premium"
                  className="relative text-xs bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200 px-3 py-1.5 rounded flex items-center gap-1 font-semibold">
                  <Bell size={12}/> Alertas <ChevronDown size={10}/>
                  {alarmasCount > 0 && (
                    <span data-testid="delegado-alarmas-badge" className="absolute -top-2 -right-2 bg-rose-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{alarmasCount}</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Alertas y análisis</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="delegado-historial-btn" onClick={() => setShowHistorial(true)} className="gap-2 cursor-pointer">
                  <FileCheck2 size={13}/> Historial de análisis
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="delegado-alarmas-contrato-btn" onClick={() => setShowAlarmas(true)} className="gap-2 cursor-pointer">
                  <AlarmClock size={13}/> Alarmas de contrato
                  {alarmasCount > 0 && <span className="ml-auto text-[10px] font-bold bg-rose-100 text-rose-700 rounded-full px-1.5">{alarmasCount}</span>}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="delegado-premium-alerts-btn" onClick={() => setShowAlerts(true)} className="gap-2 cursor-pointer">
                  <Bell size={13}/> Alertas Premium
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="delegado-preview-alerta-btn" onClick={() => setShowPreviewAlerta(true)} className="gap-2 cursor-pointer">
                  <Mail size={13}/> Vista previa de mi alerta
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Iter281 · agrupado: Mis filtros, recargar y cerrar sesión bajo "Cuenta" */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="delegado-cuenta-menu-btn"
                  title="Mis filtros, recargar y cerrar sesión"
                  className="text-xs bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200 px-3 py-1.5 rounded flex items-center gap-1 font-semibold">
                  <Settings size={12}/> Cuenta <ChevronDown size={10}/>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>{me?.nombre || user?.name}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="delegado-self-edit-btn" onClick={() => setShowSelfEdit(true)} className="gap-2 cursor-pointer">
                  <Settings size={13}/> Mis filtros
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="delegado-reload" onClick={load} disabled={loading} className="gap-2 cursor-pointer">
                  {loading ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>} {t("delegado.reload")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="delegado-logout" onClick={() => { logout(); navigate("/login"); }} className="gap-2 cursor-pointer text-rose-600">
                  <LogOut size={13}/> {t("delegado.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {showAlerts && <PremiumAlertsSettings onClose={() => setShowAlerts(false)} />}
      {showSelfEdit && (
        <DelegadoSelfEditModal
          current={me}
          onClose={() => setShowSelfEdit(false)}
          onSaved={(fresh) => { setMe(fresh); load(); }}
        />
      )}

      <main className="w-full mx-auto px-4 py-6 space-y-6">
        {/* KPIs */}
        <section className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="delegado-kpis">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
            <p className="text-[10px] uppercase text-slate-500 font-bold tracking-wider">{t("delegado.kpi.activeOpps")}</p>
            <p className="text-3xl font-extrabold text-slate-900 mt-1">{stats.total}</p>
            <p className="text-[11px] text-slate-500 mt-1">{t("delegado.kpi.activeOppsHint", { min: config?.score_min || 75 })}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-rose-400 border-y border-r border-slate-200">
            <p className="text-[10px] uppercase text-rose-700 font-bold tracking-wider flex items-center gap-1"><AlertCircle size={11}/> {t("delegado.kpi.urgent")}</p>
            <p className="text-3xl font-extrabold text-rose-700 mt-1">{stats.urg}</p>
            <p className="text-[11px] text-slate-500 mt-1">{t("delegado.kpi.urgentHint")}</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-amber-400 border-y border-r border-slate-200">
            <p className="text-[10px] uppercase text-amber-700 font-bold tracking-wider flex items-center gap-1"><Calendar size={11}/> {t("delegado.kpi.next7d")}</p>
            <p className="text-3xl font-extrabold text-amber-700 mt-1">{stats.proximas7d}</p>
            <p className="text-[11px] text-slate-500 mt-1">{t("delegado.kpi.next7dHint")}</p>
          </div>
        </section>

        {/* Filtros */}
        <section className="flex items-center gap-2 flex-wrap" data-testid="delegado-filters">
          <span className="text-xs text-slate-500 font-medium flex items-center gap-1"><Filter size={12}/> {t("delegado.filter.label")}</span>
          {[
            { key: "nuevas",      label: t("delegado.filter.new") },
            { key: "revisadas",   label: t("delegado.filter.reviewed") },
            { key: "descartadas", label: t("delegado.filter.discarded") },
            { key: "todas",       label: t("delegado.filter.all") },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilterEstado(f.key)}
              data-testid={`delegado-filter-${f.key}`}
              className={`text-xs px-3 py-1.5 rounded-full transition ${
                filterEstado === f.key
                  ? "bg-emerald-600 text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200 hover:border-emerald-300"
              }`}>
              {f.label}
            </button>
          ))}
        </section>

        {/* Lista */}
        <section className="space-y-3" data-testid="delegado-oportunidades-list">
          {itemsF.length === 0 && !loading && (
            <div className="bg-white rounded-xl p-10 text-center text-slate-500 border border-dashed border-slate-300">
              <Sparkles className="mx-auto mb-3 text-slate-300" size={32}/>
              <p className="text-sm">{t("delegado.empty.title")}</p>
              <p className="text-xs mt-1">{t("delegado.empty.hint")}</p>
            </div>
          )}

          {itemsF.map(o => {
            const sc = scoreColor(o.ai_score || 0);
            const estadoKey = ESTADO_BADGE_CFG[o.mi_estado] ? o.mi_estado : "nueva";
            const estadoCfg = ESTADO_BADGE_CFG[estadoKey];
            const estadoLabel = t(`delegado.badge.${estadoKey}`);
            const EstadoIcon = estadoCfg.Icon;
            const dias = diasRestantes(o.deadline);
            const diasColor = dias == null ? "text-slate-500" : dias < 0 ? "text-rose-700" : dias <= 3 ? "text-rose-700" : dias <= 7 ? "text-amber-700" : "text-slate-700";
            return (
              <article
                key={o.external_id}
                id={`opp-${o.external_id}`}
                data-testid={`delegado-opp-${o.external_id}`}
                className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow p-4">
                <div className="flex items-start gap-3 flex-wrap">
                  {compareMode && (
                    <input type="checkbox"
                      data-testid={`delegado-compare-check-${o.external_id}`}
                      checked={selectedCompare.includes(o.external_id)}
                      onChange={(e) => {
                        setSelectedCompare((prev) => {
                          if (e.target.checked) {
                            if (prev.length >= 3) { toast.error("Máximo 3 oportunidades para comparar"); return prev; }
                            return [...prev, o.external_id];
                          }
                          return prev.filter((id) => id !== o.external_id);
                        });
                      }}
                      className="mt-1 w-4 h-4 accent-violet-600 flex-shrink-0" />
                  )}
                  <div className={`flex-shrink-0 w-16 h-16 rounded-lg ${sc.bg} ${sc.border} border-2 flex flex-col items-center justify-center`}>
                    <span className={`text-[9px] uppercase tracking-wider ${sc.text} font-bold`}>{t("delegado.card.score")}</span>
                    <span className={`text-2xl font-extrabold ${sc.text} leading-none`}>{o.ai_score || 0}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${estadoCfg.cls} flex items-center gap-1`}>
                        <EstadoIcon size={10}/> {estadoLabel}
                      </span>
                      {o.ai_area && <span className="text-[10px] uppercase text-slate-500 font-medium">{o.ai_area.replace(/_/g, " ")}</span>}
                      <span className="text-[10px] text-slate-400">{o.source?.toUpperCase()}</span>
                    </div>
                    <h3 className="text-sm font-bold text-slate-900 leading-snug">{o.title}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">{o.organism || t("delegado.card.organismFallback")}</p>
                    <div className="flex items-center gap-4 mt-2 flex-wrap text-xs">
                      <span className="flex items-center gap-1 text-slate-600"><Euro size={11}/> {fmtEur(o.value_eur, locale)}</span>
                      <span className={`flex items-center gap-1 font-semibold ${diasColor}`}>
                        <Calendar size={11}/>
                        {o.deadline
                          ? `${o.deadline} · ${dias != null ? (dias < 0 ? t("delegado.card.daysAgo", { n: -dias }) : t("delegado.card.daysLeft", { n: dias })) : ""}`
                          : t("delegado.card.noDeadline")}
                      </span>
                      <span className="flex items-center gap-1 text-slate-500"><Sparkles size={11}/> {t("delegado.card.pub")} {o.publication_date || "—"}</span>
                    </div>
                    {o.ai_rationale && (
                      <p className="text-[11px] text-slate-500 italic mt-2 line-clamp-2">{o.ai_rationale}</p>
                    )}
                    {o.mi_comentario && (
                      <div className="mt-2 bg-emerald-50 border-l-2 border-emerald-400 px-2 py-1 rounded">
                        <p className="text-[11px] text-emerald-900"><MessageSquare size={10} className="inline mr-1"/> {o.mi_comentario}</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100 flex-wrap">
                  <a href={o.url} target="_blank" rel="noreferrer"
                     className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded flex items-center gap-1"
                     data-testid={`delegado-pliego-${o.external_id}`}>
                    <ExternalLink size={11}/> {t("delegado.card.pliegoOriginal", { src: o.source?.toUpperCase() })}
                  </a>
                  <button
                    onClick={() => setReviewing(o)}
                    className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded flex items-center gap-1"
                    data-testid={`delegado-review-${o.external_id}`}>
                    <Eye size={11}/> {o.mi_estado === "nueva" ? t("delegado.card.review") : t("delegado.card.editReview")}
                  </button>
                  <button
                    onClick={() => setAnalyzing(o)}
                    className="text-xs bg-[#007934] hover:bg-[#00682d] text-white px-3 py-1.5 rounded flex items-center gap-1"
                    data-testid={`delegado-resumen-ejecutivo-${o.external_id}`}>
                    <LayoutPanelLeft size={11}/> Resumen Ejecutivo
                  </button>
                </div>
              </article>
            );
          })}
        </section>
      </main>

      {reviewing && <RevisionModal opp={reviewing} onClose={(refresh) => { setReviewing(null); if (refresh) load(); }}/>}

      <Sheet open={!!analyzing} onOpenChange={(open) => { if (!open) setAnalyzing(null); }}>
        <SheetContent side="left" className="w-full sm:max-w-xl overflow-y-auto" data-testid="delegado-resumen-sheet">
          <SheetHeader>
            <SheetTitle className="text-left">Resumen Ejecutivo</SheetTitle>
            <SheetDescription className="text-left">KPIs, mapa, composición de oferta, curva económica y adjudicador de esta oportunidad.</SheetDescription>
          </SheetHeader>
          {analyzing && <div className="mt-4"><ResumenEjecutivoDelegado opp={analyzing} /></div>}
        </SheetContent>
      </Sheet>

      <Sheet open={showMapa} onOpenChange={setShowMapa}>
        <SheetContent side="right" className="w-full sm:max-w-2xl overflow-y-auto" data-testid="delegado-mapa-sheet">
          <SheetHeader>
            <SheetTitle className="text-left">Mapa de mi zona</SheetTitle>
            <SheetDescription className="text-left">Todas las oportunidades de tu zona geolocalizadas en un único mapa.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            <MapaZonaDelegado onSelectPin={(extId) => {
              const opp = items.find((o) => o.external_id === extId);
              if (opp) { setShowMapa(false); setAnalyzing(opp); }
            }} />
          </div>
        </SheetContent>
      </Sheet>

      {compareMode && selectedCompare.length >= 2 && (
        <button
          onClick={() => setShowComparador(true)}
          data-testid="delegado-comparar-seleccionadas-btn"
          className="fixed bottom-6 right-6 bg-violet-600 hover:bg-violet-700 text-white px-5 py-3 rounded-full shadow-lg font-semibold text-sm flex items-center gap-2 z-30">
          <Layers size={16}/> Comparar seleccionadas ({selectedCompare.length})
        </button>
      )}

      <Sheet open={showComparador} onOpenChange={setShowComparador}>
        <SheetContent side="bottom" className="w-full h-[90vh] overflow-y-auto" data-testid="delegado-comparador-sheet">
          <SheetHeader>
            <SheetTitle className="text-left">Comparador de ofertas</SheetTitle>
            <SheetDescription className="text-left">KPIs lado a lado de las oportunidades seleccionadas.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {showComparador && <ComparadorOfertas externalIds={selectedCompare} />}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={showHistorial} onOpenChange={setShowHistorial}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="delegado-historial-sheet">
          <SheetHeader>
            <SheetTitle className="text-left">Historial de análisis</SheetTitle>
            <SheetDescription className="text-left">Pliegos ya analizados con IA para no repetir trabajo.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {showHistorial && (
              <HistorialAnalisis onOpen={(extId) => {
                const opp = items.find((o) => o.external_id === extId);
                setShowHistorial(false);
                setAnalyzing(opp || { external_id: extId });
              }} />

            )}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={showAlarmas} onOpenChange={setShowAlarmas}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="delegado-alarmas-sheet">
          <SheetHeader>
            <SheetTitle className="text-left">Alarmas de contrato</SheetTitle>
            <SheetDescription className="text-left">Contratos de tu zona superiores a 1M€ que finalizan en menos de 2 años. También se envían por email cada lunes.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {showAlarmas && <AlarmasContrato />}
          </div>
        </SheetContent>
      </Sheet>

      <Sheet open={showPreviewAlerta} onOpenChange={setShowPreviewAlerta}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="delegado-preview-alerta-sheet">
          <SheetHeader>
            <SheetTitle className="text-left">Vista previa de mi alerta</SheetTitle>
            <SheetDescription className="text-left">Lo que te llegaría por email HOY si se disparara ahora mismo el aviso diario de tu zona.</SheetDescription>
          </SheetHeader>
          <div className="mt-4">
            {showPreviewAlerta && <PreviewMiAlerta />}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}


function RevisionModal({ opp, onClose }) {
  const { t } = useTranslation();
  const [estado, setEstado]         = useState(opp.mi_estado === "nueva" ? "revisada" : opp.mi_estado);
  const [comentario, setComentario] = useState(opp.mi_comentario || "");
  const [saving, setSaving]         = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/delegado/oportunidades/revision`, {
        external_id: opp.external_id, estado, comentario,
      });
      toast.success(t("delegado.modal.saveSuccess", { estado: t(`delegado.badge.${estado}`) }));
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("delegado.toast.saveError"));
    } finally { setSaving(false); }
  };

  const estadoLabels = {
    revisada:   t("delegado.modal.reviewedBtn"),
    descartada: t("delegado.modal.discardedBtn"),
    nueva:      t("delegado.modal.newBtn"),
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => onClose(false)} data-testid="revision-modal">
      <div className="bg-white rounded-xl max-w-lg w-full p-5 shadow-2xl" onClick={e => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-slate-900 mb-3">{t("delegado.modal.title")}</h3>
        <p className="text-xs text-slate-600 mb-4 line-clamp-2"><b>{opp.title}</b><br/><span className="text-slate-500">{opp.organism}</span></p>

        <label className="block mb-3">
          <span className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">{t("delegado.modal.estado")}</span>
          <div className="flex gap-2 mt-1.5">
            {["revisada","descartada","nueva"].map(s => (
              <button
                key={s}
                onClick={() => setEstado(s)}
                data-testid={`revision-estado-${s}`}
                className={`flex-1 text-xs px-3 py-2 rounded border-2 capitalize transition ${
                  estado === s
                    ? "border-emerald-500 bg-emerald-50 text-emerald-800 font-semibold"
                    : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                }`}>
                {estadoLabels[s]}
              </button>
            ))}
          </div>
        </label>

        <label className="block mb-4">
          <span className="text-[10px] uppercase font-bold text-slate-600 tracking-wider">{t("delegado.modal.comentario")}</span>
          <textarea
            value={comentario}
            onChange={e => setComentario(e.target.value)}
            rows={3}
            placeholder={t("delegado.modal.comentarioPlaceholder")}
            data-testid="revision-comentario"
            className="w-full text-xs border border-slate-200 rounded p-2 mt-1.5 focus:border-emerald-400 focus:outline-none"
          />
        </label>

        <div className="flex justify-end gap-2">
          <button onClick={() => onClose(false)} className="text-xs border border-slate-200 px-3 py-1.5 rounded text-slate-600 hover:bg-slate-50" data-testid="revision-cancel">{t("common.cancel")}</button>
          <button onClick={save} disabled={saving} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded flex items-center gap-1 disabled:opacity-50" data-testid="revision-save">
            {saving ? <Loader2 size={11} className="animate-spin"/> : null}
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}


// ═════════════════════════════════════════════════════════════════════════
// Iter 127 — DelegadoSelfEditModal
// Score min / high value floor / áreas se aplican inmediato.
// Provincias y comunidades quedan como `pending_changes` → aprobación admin.
// ═════════════════════════════════════════════════════════════════════════
const AVAILABLE_AI_AREAS = [
  "residuos", "recogida", "transporte_residuos", "limpieza_viaria",
  "contenerizacion", "plantas_tratamiento", "zonas_verdes", "movilidad",
];
const PROVINCIAS_ES = [
  "A Coruña","Álava","Albacete","Alicante","Almería","Asturias","Ávila","Badajoz","Baleares",
  "Barcelona","Burgos","Cáceres","Cádiz","Cantabria","Castellón","Ceuta","Ciudad Real","Córdoba",
  "Cuenca","Girona","Granada","Guadalajara","Guipúzcoa","Huelva","Huesca","Jaén","La Rioja",
  "Las Palmas","León","Lleida","Lugo","Madrid","Málaga","Melilla","Murcia","Navarra","Ourense",
  "Palencia","Pontevedra","Salamanca","Santa Cruz de Tenerife","Segovia","Sevilla","Soria",
  "Tarragona","Teruel","Toledo","Valencia","Valladolid","Vizcaya","Zamora","Zaragoza",
];

function DelegadoSelfEditModal({ current, onClose, onSaved }) {
  const [scoreMin, setScoreMin]         = useState(current?.score_min ?? 75);
  const [highValueFloor, setHighValueFloor] = useState(current?.high_value_floor ?? 5000000);
  const [aiAreas, setAiAreas]           = useState(current?.ai_areas || AVAILABLE_AI_AREAS);
  const [provincias, setProvincias]     = useState(current?.provincias || []);
  const [changeReason, setChangeReason] = useState("");
  const [saving, setSaving]             = useState(false);

  const hasZoneChange = JSON.stringify([...provincias].sort()) !==
                        JSON.stringify([...(current?.provincias || [])].sort());

  const toggleArea = (a) =>
    setAiAreas(aiAreas.includes(a) ? aiAreas.filter(x => x !== a) : [...aiAreas, a]);
  const toggleProv = (p) =>
    setProvincias(provincias.includes(p) ? provincias.filter(x => x !== p) : [...provincias, p]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        score_min:         scoreMin,
        high_value_floor:  Number(highValueFloor) || 0,
        ai_areas:          aiAreas,
      };
      if (hasZoneChange) {
        payload.provincias = provincias;
        payload.change_reason = changeReason;
      }
      const r = await axios.put(`${API}/delegado/me`, payload);
      const direct = r.data?.applied_direct || [];
      const pendingId = r.data?.pending_zone_id;
      if (direct.length && !pendingId) toast.success("Filtros aplicados");
      else if (pendingId)              toast.success("Filtros aplicados · zona pendiente de aprobación admin");
      else                             toast.info("Sin cambios que guardar");
      onSaved?.(r.data.delegado);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="delegado-self-edit-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 text-white px-6 py-4 flex items-center gap-2 rounded-t-2xl">
          <Settings size={18}/>
          <div>
            <h3 className="font-bold text-lg">Ajustar mis filtros</h3>
            <p className="text-xs opacity-90">Score Pando · High-value · Áreas se aplican al instante · Zona requiere admin</p>
          </div>
        </div>

        <div className="p-6 space-y-5">
          {/* Cambios pendientes previos */}
          {current?.pending_changes && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
              <b>Cambio pendiente:</b> zona propuesta {JSON.stringify(current.pending_changes.proposed)}
              (solicitado {String(current.pending_changes.requested_at || "").slice(0, 10)}).
              Espera aprobación de admin.
            </div>
          )}

          {/* Score min */}
          <div>
            <div className="flex justify-between text-xs font-bold text-slate-700 mb-1">
              <span>Score Pando mínimo</span>
              <span className="text-indigo-600 tabular-nums" data-testid="self-edit-score-min-value">{scoreMin}</span>
            </div>
            <input
              type="range" min="0" max="100" step="5"
              value={scoreMin}
              onChange={e => setScoreMin(parseInt(e.target.value))}
              className="w-full accent-indigo-600"
              data-testid="self-edit-score-min"/>
            <p className="text-[11px] text-slate-500 mt-1">
              Solo verás oportunidades con score Pando ≥ {scoreMin}. Bajar amplía el pipeline
              pero mete más ruido.
            </p>
          </div>

          {/* High value */}
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-1">Umbral high-value (€)</label>
            <input
              type="number" step="100000" min="0"
              value={highValueFloor}
              onChange={e => setHighValueFloor(e.target.value)}
              className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
              data-testid="self-edit-high-value"/>
            <p className="text-[11px] text-slate-500 mt-1">
              Ofertas por encima de {(Number(highValueFloor) / 1_000_000).toFixed(1)} M€ pasan
              siempre el filtro, ignorando score_min.
            </p>
          </div>

          {/* Áreas temáticas */}
          <div>
            <label className="text-xs font-bold text-slate-700 block mb-2">Áreas temáticas que me interesan</label>
            <div className="flex flex-wrap gap-1.5">
              {AVAILABLE_AI_AREAS.map(a => (
                <button
                  key={a}
                  type="button"
                  onClick={() => toggleArea(a)}
                  data-testid={`self-edit-area-${a}`}
                  className={`text-[10px] px-2 py-1 rounded-full font-semibold ${
                    aiAreas.includes(a)
                      ? "bg-indigo-100 text-indigo-800 border border-indigo-300"
                      : "bg-slate-50 text-slate-500 border border-slate-200"
                  }`}>{a}</button>
              ))}
            </div>
          </div>

          {/* Provincias */}
          <div>
            <label className="text-xs font-bold text-slate-700 flex items-center gap-1 mb-1">
              Zona geográfica (provincias)
              <span className="text-[10px] font-normal text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                requiere aprobación admin
              </span>
            </label>
            <div className="max-h-36 overflow-y-auto border border-slate-200 rounded p-2 grid grid-cols-3 gap-1.5">
              {PROVINCIAS_ES.map(p => (
                <label key={p} className="text-[11px] text-slate-700 flex items-center gap-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={provincias.includes(p)}
                    onChange={() => toggleProv(p)}
                    className="accent-indigo-600"
                    data-testid={`self-edit-prov-${p}`}/>
                  {p}
                </label>
              ))}
            </div>
            {hasZoneChange && (
              <div className="mt-2">
                <label className="text-[11px] text-amber-800 block mb-0.5 font-semibold">
                  Motivo del cambio de zona (para admin):
                </label>
                <textarea
                  rows="2"
                  value={changeReason}
                  onChange={e => setChangeReason(e.target.value)}
                  className="w-full text-xs border border-amber-300 rounded p-2 focus:border-amber-500 focus:outline-none"
                  data-testid="self-edit-zone-reason"
                  placeholder="Ej. reasignación por baja del delegado de Ourense"/>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-100 px-6 py-3 flex justify-end gap-2 bg-slate-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="text-xs border border-slate-200 bg-white px-4 py-1.5 rounded text-slate-600 hover:bg-slate-100"
            data-testid="self-edit-cancel">Cancelar</button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="self-edit-save"
            className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded flex items-center gap-1 disabled:opacity-50">
            {saving ? <Loader2 size={11} className="animate-spin"/> : null}
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}
